// src/utils/pdf.js
import html2pdf from "html2pdf.js";

/**
 * Attendre que le DOM soit vraiment prêt pour la capture :
 * - images chargées
 * - polices chargées
 * - layout stabilisé
 */
async function waitReady(rootEl) {
    if (!rootEl) return;

    // 1) images
    const imgs = Array.from(rootEl.querySelectorAll("img"));
    await Promise.all(
        imgs.map((img) =>
            img.complete
                ? Promise.resolve()
                : new Promise((res) => {
                    img.onload = img.onerror = res;
                })
        )
    );

    // 2) polices
    try {
        if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
        }
    } catch {
        // on ignore si non supporté
    }

    // 3) 2 ticks pour laisser respirer le layout
    await new Promise((r) => setTimeout(r, 0));
    await new Promise((r) => requestAnimationFrame(() => r()));
}

/**
 * Génère un Blob PDF à partir d'une page A4:
 * - réserve la place du footer
 * - insère le footer en image sur la DERNIÈRE page seulement
 * - ajoute la numérotation "Page X/Y"
 */
export async function buildPdfBlobWithLastFooterAndPages(
    rootEl,
    footerEl,       // on le garde en signature pour compatibilité, même si on ne l'utilise plus
    opts = {}
) {
    if (!rootEl) {
        throw new Error("rootEl manquant");
    }

    const { filename = "document.pdf" } = opts;

    // Style inline spécial export (évite les coupures moches dans les ql-editor et tableaux)
    const style = document.createElement("style");
    style.id = "pdf-inline-style";
    style.textContent = `
    .exporting-pdf .ql-editor::after {
      content: "";
      display: block;
      height: 6mm;
    }
    .exporting-pdf .ql-editor p {
      break-inside: avoid;
    }

    /* Empêche html2pdf de couper les tableaux en deux */
    .exporting-pdf .table-block,
    .exporting-pdf table.table-pdf {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }

    /* Force tous les tableaux à occuper toute la largeur dispo */
    .exporting-pdf .table-block {
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .exporting-pdf table.table-pdf {
      width: 100% !important;
      table-layout: fixed !important;
      border-collapse: collapse !important;
      border-spacing: 0 !important;
    }
  `;
    document.head.appendChild(style);

    // classes globales (utilisées aussi par ton CSS)
    document.body.classList.add("exporting-pdf");
    rootEl.classList.add("exporting-pdf");

    // on NE cache PLUS le footer HTML : on veut garder celui de .a4-footer tel quel
    // on NE modifie PLUS le paddingBottom ici : ton CSS gère déjà l'espace footer

    // Laisser le temps aux images / polices / layout de se stabiliser
    await waitReady(rootEl);

    try {
        // ---- Génération du PDF via html2pdf ----
        const worker = html2pdf()
            .set({
                // marges [top, left, bottom, right] en mm
                margin: [10, 0, 0, 0],
                filename,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                pagebreak: { mode: ["css", "legacy"] }, // respecte .no-split, etc.
            })
            .from(rootEl)
            .toPdf();

        const pdf = await worker.get("pdf");

        // ---- Pagination (numérotation des pages uniquement) ----
        const total = pdf.internal.getNumberOfPages();
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const marginX = 10;

        for (let i = 1; i <= total; i++) {
            pdf.setPage(i);
            pdf.setFontSize(9);

            // Numérotation des pages (en bas à droite)
            pdf.text(`Page ${i}/${total}`, pageW - marginX, pageH - 5, {
                align: "right",
            });

            // IMPORTANT : plus d'addImage ici → on ne rajoute PAS de footer image.
            // Le seul footer visible dans le PDF est celui déjà rendu dans .a4-footer (FooterApercu).
        }

        return pdf.output("blob");
    } finally {
        // ---- Restauration du DOM dans tous les cas ----
        rootEl.classList.remove("exporting-pdf");
        document.body.classList.remove("exporting-pdf");

        const styleNode = document.getElementById("pdf-inline-style");
        if (styleNode) {
            styleNode.remove();
        }
    }
}

export async function exportPdfWithLastFooterAndPages(
    rootEl,
    footerEl,
    filename
) {
    const blob = await buildPdfBlobWithLastFooterAndPages(rootEl, footerEl, {
        filename,
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    a.click();
    URL.revokeObjectURL(url);
}
